// Reiter „Verwaltung“ in einfachem JavaScript (Probe, siehe `kit.js`). Erzeugt dasselbe Markup wie `AdminTab.tsx`; ein Test vergleicht beide.
// Der Abschnitt „Erkennung verbessern“ ist eine eigene Komponente der App und steht in einem Platzhalter (data-slot="ocr-tests").
import { h } from './kit.js';

const IMPORT_ACCEPT = '.xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function importBadge(item) {
  return item.state === 'neu' ? 'erfolg' : item.state === 'fehler' ? 'fehler' : item.state === 'doppelt' ? 'warnung' : '';
}

function importLabel(item) {
  return item.state === 'neu' ? 'neu' : item.state === 'vorhanden' ? 'schon vorhanden' : item.state === 'doppelt' ? 'doppelt in Datei' : 'fehlerhaft';
}

function yearSelect(years, selected, attributes) {
  return h('select', attributes, years.map(y => h('option', { value: y, selected: y === selected }, y)));
}

/** Abschnitt „Fotos nachträglich auswerten“ (auch im Reiter „Auswertung“ der React-Fassung benutzt). */
export function backfillSection(s, backfill) {
  const counts = backfill.counts;
  return h('section', { class: 'wb-form-section spaced' },
    h('h2', {}, 'Fotos nachträglich auswerten'),
    h('label', { class: 'year-field' }, 'Jahr', yearSelect(s.yearOptions, s.year, { 'data-change': 'year' })),
    h('p', { class: 'wb-help hint' }, 'Liest den Zählerstand von bereits gespeicherten Zählerfotos des gewählten Jahres und trägt ihn in die OCR-Spalten der Liste ein; der erfasste Zählerstand und der Status bleiben unverändert. So entsteht der Vergleich zwischen erfasstem und erkanntem Wert für die Optimierung der Erkennung. Azure erlaubt im Free-Tarif 20 Aufrufe pro Minute, deshalb dauert es etwas.'),
    counts && h('p', { class: 'wb-notice info', role: 'status' },
      `${counts.withPhoto} Ablesungen mit Foto · ${counts.evaluated} ausgewertet · ${counts.pending} offen`
      + (counts.evaluated > 0 ? ` · erkannter Wert stimmt bei ${counts.matched} von ${counts.evaluated} (${Math.round(100 * counts.matched / counts.evaluated)} %), weicht ab bei ${counts.differing}, nichts erkannt bei ${counts.noValue}` : '')),
    h('div', { class: 'wb-actions' },
      !backfill.running && h('button', { class: 'wb-secondary', 'data-click': 'backfill-missing', disabled: s.busy || !counts || counts.pending === 0 }, `Offene Fotos auswerten${counts ? ` (${counts.pending})` : ''}`),
      !backfill.running && h('button', { class: 'wb-secondary', 'data-click': 'backfill-recompute', disabled: s.busy || !counts || counts.evaluated === 0 }, 'Erkennung aus Rohtext neu berechnen'),
      backfill.running && h('button', { class: 'wb-secondary', 'data-click': 'backfill-stop' }, 'Anhalten')),
    backfill.message && h('p', { class: 'wb-help hint', role: 'status' }, backfill.message));
}

function importPreviewBlock(s) {
  const preview = s.importPreview;
  if (!preview) return null;
  const result = preview.result;
  const rows = result.items.slice(0, 200).map(item => h('tr', {},
    h('td', {}, String(item.line)),
    h('td', {}, `${item.jahr ?? ''}${item.jahrQuelle === 'import' ? ' (Import)' : ''}`),
    h('td', {}, item.strasse ?? ''),
    h('td', {}, item.zaehlernummer ?? ''),
    h('td', {}, h('span', { class: `wb-badge ${importBadge(item)}` }, importLabel(item)), item.state === 'fehler' && item.message ? ` ${item.message}` : '')));
  return h('div', { 'aria-live': 'polite' },
    h('p', { class: 'label-title' }, 'Vorschau'),
    h('p', { class: 'wb-help hint' }, `${preview.fileName}: ${result.total} Zeile(n) geprüft, noch nichts angelegt.`),
    h('p', { class: `wb-notice ${result.errors.length ? 'warnung' : 'info'}` },
      `${result.created} neu · ${result.skipped} schon vorhanden oder doppelt (werden übersprungen) · ${result.errors.length} fehlerhaft (werden nicht importiert)`),
    (result.withoutYear ?? 0) > 0 && result.defaultYear && h('p', { class: 'wb-notice info' },
      `${result.withoutYear} Zeile(n) ohne Importjahr erhalten ${result.defaultYear}${result.yearsInFile && result.yearsInFile.length ? `; Zeilen mit eigenem Jahr (${result.yearsInFile.join(', ')}) bleiben unverändert` : ''}.`),
    result.items.length > 0 && h('div', { class: 'wb-table-wrap' }, h('table', { class: 'wb-table' },
      h('thead', {}, h('tr', {}, h('th', {}, 'Zeile'), h('th', {}, 'Jahr'), h('th', {}, 'Straße'), h('th', {}, 'Zählernummer'), h('th', {}, 'Ergebnis'))),
      h('tbody', {}, rows))),
    result.items.length > 200 && h('p', { class: 'wb-help hint' }, `Es werden die ersten 200 von ${result.items.length} Zeilen angezeigt.`),
    h('div', { class: 'wb-actions import-actions' },
      h('label', { class: 'year-field full' }, 'Jahr für Zeilen ohne Importjahr',
        h('select', { disabled: s.busy, 'data-change': 'import-year' },
          h('option', { value: '', selected: s.importYear === '' }, 'Keines'),
          [s.yearNow - 1, s.yearNow, s.yearNow + 1].map(y => h('option', { value: y, selected: String(y) === s.importYear }, y))))),
    h('small', { class: 'wb-help hint' }, 'Nur leere Zellen der Spalte „Importjahr“ erhalten dieses Jahr; vorhandene Jahre in der Datei bleiben unverändert. Eine Änderung rechnet die Vorschau neu.'),
    h('div', { class: 'wb-actions import-actions' },
      h('button', { class: 'wb-primary', 'data-click': 'import-confirm', disabled: s.busy || result.created === 0 }, `${result.created} neue Aufträge importieren`),
      h('button', { class: 'wb-secondary', 'data-click': 'import-cancel', disabled: s.busy }, 'Abbrechen')));
}

function importSection(s) {
  return h('section', { class: 'wb-form-section spaced' },
    h('h2', {}, 'Import'),
    h('p', { class: 'wb-help hint' }, 'Jährliche Ableseliste (Excel-Vorlage oder CSV) einspielen. Vor dem Anlegen zeigt eine Vorschau, was passiert.'),
    h('div', { class: 'wb-field' },
      h('input', { hidden: true, id: 'import-datei', type: 'file', accept: IMPORT_ACCEPT, disabled: s.busy, 'data-change': 'import-file' }),
      h('label', { for: 'import-datei', class: 'wb-file-button' }, 'Ableseliste wählen')),
    s.importDone && h('p', { class: `wb-notice ${s.importDone.ok ? 'info' : 'warnung'}`, role: 'status' }, s.importDone.text),
    importPreviewBlock(s));
}

function exportSection(s) {
  const noColumns = s.exportScope === 'auswahl' && s.exportColumns.length === 0;
  return h('section', { class: 'wb-form-section spaced' },
    h('h2', {}, 'Export'),
    h('p', { class: 'wb-help hint' }, 'Erfasste Zählerstände eines Ablesejahres als Excel oder CSV, zum Beispiel für die Jahresmeldung an die Versorger.'),
    h('div', { class: 'wb-grid pair' },
      h('div', { class: 'wb-field' }, h('label', { for: 'export-year' }, 'Jahr'), yearSelect(s.yearOptions, s.year, { id: 'export-year', 'data-change': 'year' })),
      h('div', { class: 'wb-field' }, h('label', { for: 'export-status' }, 'Status'),
        h('select', { id: 'export-status', 'data-change': 'export-status' }, s.statusOptions.map(option => h('option', { value: option.value, selected: option.value === s.exportStatus }, option.label))))),
    h('div', { class: 'wb-field-group' },
      h('span', { id: 'export-spalten-label', class: 'label-title' }, 'Spalten'),
      h('div', { class: 'wb-chips two-columns', role: 'group', 'aria-labelledby': 'export-spalten-label' },
        s.scopes.map(scope => h('button', { type: 'button', class: `wb-chip${s.exportScope === scope.value ? ' active' : ''}`, 'aria-pressed': String(s.exportScope === scope.value), 'data-click': 'export-scope', 'data-value': scope.value }, scope.label))),
      h('small', { class: 'wb-help hint' }, s.scopeHint)),
    s.exportScope === 'auswahl' && h('div', { class: 'wb-field-group' },
      h('div', { class: 'wb-chips', role: 'group', 'aria-label': 'Spalten auswählen' },
        s.columnChoices.map(([label, key]) => h('label', { class: 'wb-option' }, h('input', { type: 'checkbox', checked: s.exportColumns.includes(key), 'data-change': 'export-column', 'data-value': key }), label))),
      h('div', { class: 'wb-actions' },
        h('button', { type: 'button', class: 'wb-secondary small', 'data-click': 'columns-all' }, 'Alle auswählen'),
        h('button', { type: 'button', class: 'wb-secondary small', 'data-click': 'columns-none' }, 'Keine'),
        h('button', { type: 'button', class: 'wb-secondary small', 'data-click': 'columns-versorger' }, 'Wie für den Versorger'))),
    h('div', { class: 'wb-actions' },
      h('button', { class: 'wb-secondary', 'data-click': 'export-xlsx', disabled: s.busy || noColumns }, 'Als Excel exportieren'),
      h('button', { class: 'wb-secondary', 'data-click': 'export-csv', disabled: s.busy || noColumns }, 'Als CSV exportieren')));
}

/** Ansicht des Reiters „Verwaltung“ zu einem Zustand (`AdminState`). */
export function adminView(s) {
  return h('main', { class: 'wb-panel wb-page', role: 'tabpanel', id: 'panel-verwaltung', 'aria-labelledby': 'tab-verwaltung' },
    importSection(s), exportSection(s), s.backfill && backfillSection(s, s.backfill), h('div', { 'data-slot': 'ocr-tests' }));
}

/** Reiter „Auswertung“ für Nutzer ohne Verwaltungsrolle (nur „Fotos nachträglich auswerten“). */
export function auswertungView(s) {
  return h('main', { class: 'wb-panel wb-page', role: 'tabpanel', id: 'panel-auswertung', 'aria-labelledby': 'tab-auswertung' }, backfillSection(s, s.backfill));
}

/** Ordnet die Ereignisse des Markups (data-Attribute) den Aktionen der App zu (`AdminActions` in `AdminTab.tsx`). */
export function adminActions(a) {
  return {
    'import-file': el => { const file = el.files && el.files[0]; el.value = ''; a.pickImportFile(file); },
    'import-year': el => a.setImportYear(el.value),
    'import-confirm': () => a.confirmImport(),
    'import-cancel': () => a.cancelImport(),
    year: el => a.setYear(Number(el.value)),
    'export-status': el => a.setExportStatus(el.value),
    'export-scope': el => a.setExportScope(el.getAttribute('data-value')),
    'export-column': el => a.toggleColumn(el.getAttribute('data-value'), el.checked),
    'columns-all': () => a.selectAllColumns(),
    'columns-none': () => a.clearColumns(),
    'columns-versorger': () => a.versorgerColumns(),
    'export-xlsx': () => a.runExport('xlsx'),
    'export-csv': () => a.runExport('csv'),
    'backfill-missing': () => a.runBackfill('missing'),
    'backfill-recompute': () => a.runBackfill('recompute'),
    'backfill-stop': () => a.stopBackfill()
  };
}
